import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Alert, Badge, Box, Button, CircularProgress, FormControl,
  IconButton, InputAdornment, InputLabel, MenuItem, Select, Stack,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined, ArrowDownward, ArrowUpward, Clear, ExpandLess, ExpandMore, FilterList,
  LockOutlined, Search,
} from '@mui/icons-material';
import { flexRender, getCoreRowModel, useReactTable, SortingState } from '@tanstack/react-table';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { AccountMovement, Company, Debt, DebtSearchParams, PaginatedResponse, Role, User } from '../../types';
import { apiClient } from '../../services/api';
import { formatDateTime } from '../../utils/date';
import { tableSortFromSorting } from '../../utils/tableSort';
import { createTextActionSx } from '../../theme/interactions';
import ColumnVisibilityMenu from '../common/ColumnVisibilityMenu';
import HelpTooltip from '../common/HelpTooltip';
import PageHeader from '../common/PageHeader';
import PageHeaderActionButton from '../common/PageHeaderActionButton';
import { useIsMobileShell } from '../common/useIsMobileShell';
import DataTableHeaderCell from '../common/DataTableHeaderCell';
import { useMobileTopBarAction, useMobileTopBarActions } from '../common/MobileTopBarActionsContext';
import PaginationFooter from '../common/PaginationFooter';
import ListSurface from '../common/ListSurface';
import TableFilterPanel from '../common/TableFilterPanel';
import NotebookColumnTab from '../common/NotebookColumnTab';
import DebtActionsMenu from './DebtActionsMenu';
import DebtDialog from './DebtDialog';
import DeleteDebtDialog from './DeleteDebtDialog';
import PaymentDialog from './PaymentDialog';
import { createDebtColumns, filterDebtColumns, isSaleLinked, MovementAmount, MovementIcon } from './debtColumns';
import { accountMovementDescription, toEditableDebt } from '../../utils/accountMovement';

interface DebtListProps {
  paginatedData: PaginatedResponse<AccountMovement> | null;
  loading: boolean;
  searchParams: DebtSearchParams;
  onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
  onSortChange: (field?: string, direction?: 'asc' | 'desc') => void;
  onFilterChange: (field: keyof DebtSearchParams, value: DebtSearchParams[keyof DebtSearchParams]) => void;
  onFiltersChange: (filters: Partial<DebtSearchParams>) => void;
  onCustomerClick?: (customerId: string, customerName: string) => void;
}

export interface DebtListRef {
  handleAddDebt: () => void;
  handleAddPayment: () => void;
}

const filterFields = ['startDate', 'endDate', 'minAmount', 'maxAmount', 'createdBy', 'companyId'] as const;

const DebtList = forwardRef<DebtListRef, DebtListProps>(({
  paginatedData, loading, searchParams, onPaginationModelChange, onSortChange,
  onFilterChange, onFiltersChange, onCustomerClick,
}, ref) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isMobile = useIsMobileShell();
  const canManageDebts = user?.role === Role.COMPANY_ADMIN || user?.role === Role.EMPLOYEE;
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;
  const [searchTerm, setSearchTerm] = useState(searchParams.search || '');
  const [showFilters, setShowFilters] = useState(false);
  const [startDateDraft, setStartDateDraft] = useState(searchParams.startDate || '');
  const [endDateDraft, setEndDateDraft] = useState(searchParams.endDate || '');
  const [minAmountDraft, setMinAmountDraft] = useState(searchParams.minAmount?.toString() || '');
  const [maxAmountDraft, setMaxAmountDraft] = useState(searchParams.maxAmount?.toString() || '');
  const [createdByDraft, setCreatedByDraft] = useState(searchParams.createdBy || '');
  const [companyDraft, setCompanyDraft] = useState(searchParams.companyId || '');
  const [filterError, setFilterError] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    customer: true, debtType: true, description: true, saleNumber: true,
    lastActivityDateTime: true, amount: true, createdByUsername: false,
    companyName: false, actions: true,
  });
  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState<Debt | undefined>();
  const [debtToDelete, setDebtToDelete] = useState<Debt | null>(null);
  const [saleMovementNoticeOpen, setSaleMovementNoticeOpen] = useState(false);

  useEffect(() => { setStartDateDraft(searchParams.startDate || ''); }, [searchParams.startDate]);
  useEffect(() => { setEndDateDraft(searchParams.endDate || ''); }, [searchParams.endDate]);
  useEffect(() => { setMinAmountDraft(searchParams.minAmount?.toString() || ''); }, [searchParams.minAmount]);
  useEffect(() => { setMaxAmountDraft(searchParams.maxAmount?.toString() || ''); }, [searchParams.maxAmount]);
  useEffect(() => { setCreatedByDraft(searchParams.createdBy || ''); }, [searchParams.createdBy]);
  useEffect(() => { setCompanyDraft(searchParams.companyId || ''); }, [searchParams.companyId]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let active = true;
    Promise.allSettled([apiClient.getUsers(), apiClient.getCompanies()]).then(([userResult, companyResult]) => {
      if (!active) return;
      setUsers(userResult.status === 'fulfilled' ? userResult.value : []);
      setCompanies(companyResult.status === 'fulfilled' ? companyResult.value : []);
    });
    return () => { active = false; };
  }, [isSuperAdmin]);

  const handleAddDebt = () => { setSelectedDebt(undefined); setDebtDialogOpen(true); };
  const handleAddPayment = () => setPaymentDialogOpen(true);
  useImperativeHandle(ref, () => ({ handleAddDebt, handleAddPayment }));

  const mobileTopBarActions = useMobileTopBarActions();
  useMobileTopBarAction(mobileTopBarActions && canManageDebts ? {
    id: 'debts-receive-payment',
    label: t('debts.addPayment'),
    icon: <ArrowDownward fontSize="small" />,
    order: 10,
    onSelect: () => handleAddPayment(),
  } : null);
  useMobileTopBarAction(mobileTopBarActions && canManageDebts ? {
    id: 'debts-add-debt',
    label: t('debts.addDebt'),
    icon: <ArrowUpward fontSize="small" />,
    order: 20,
    onSelect: () => handleAddDebt(),
  } : null);

  const handleCustomerClick = useCallback((debt: AccountMovement) => {
    if (debt.customerId) onCustomerClick?.(debt.customerId, debt.customerName);
  }, [onCustomerClick]);
  const handleEditDebt = (movement: AccountMovement) => { setSelectedDebt(toEditableDebt(movement)); setDebtDialogOpen(true); };
  const handleDeleteDebt = (movement: AccountMovement) => setDebtToDelete(toEditableDebt(movement));
  const handleSaleMovementLocked = useCallback(() => setSaleMovementNoticeOpen(true), []);

  const appliedFilterCount = filterFields.filter(field => searchParams[field] !== undefined && searchParams[field] !== '').length;
  const hasAnyFilter = appliedFilterCount > 0 || !!searchParams.search;
  const totalElements = paginatedData?.totalElements ?? 0;
  const totalPages = paginatedData?.totalPages ?? 0;
  const page = searchParams.page ?? 0;
  const pageSize = searchParams.size ?? 10;

  const applyFilters = () => {
    const minAmount = minAmountDraft.trim() ? Number(minAmountDraft) : undefined;
    const maxAmount = maxAmountDraft.trim() ? Number(maxAmountDraft) : undefined;
    if ((minAmount !== undefined && (!Number.isFinite(minAmount) || minAmount < 0))
      || (maxAmount !== undefined && (!Number.isFinite(maxAmount) || maxAmount < 0))
      || (minAmount !== undefined && maxAmount !== undefined && minAmount > maxAmount)) {
      setFilterError(t('debts.invalidAmountRange'));
      return;
    }
    if (startDateDraft && endDateDraft && startDateDraft > endDateDraft) {
      setFilterError(t('debts.invalidDateRange'));
      return;
    }
    setFilterError('');
    onFiltersChange({ startDate: startDateDraft || undefined, endDate: endDateDraft || undefined, minAmount, maxAmount,
      createdBy: createdByDraft || undefined, companyId: companyDraft || undefined });
    setShowFilters(false);
  };

  const clearFilters = () => {
    setStartDateDraft(''); setEndDateDraft(''); setMinAmountDraft(''); setMaxAmountDraft('');
    setCreatedByDraft(''); setCompanyDraft('');
    setSearchTerm(''); setFilterError('');
    onFiltersChange({ search: undefined, startDate: undefined, endDate: undefined,
      minAmount: undefined, maxAmount: undefined, createdBy: undefined, companyId: undefined });
  };

  const allColumns = useMemo(() => createDebtColumns({
    t, language, canManageDebts, isSuperAdmin,
    onCustomerClick: handleCustomerClick,
    onEdit: handleEditDebt,
    onDelete: handleDeleteDebt,
    onSaleMovementLocked: handleSaleMovementLocked,
  }), [t, language, canManageDebts, isSuperAdmin, handleCustomerClick, handleSaleMovementLocked]);
  const columns = useMemo(() => filterDebtColumns(allColumns, columnVisibility, isSuperAdmin, canManageDebts),
    [allColumns, columnVisibility, isSuperAdmin, canManageDebts]);
  const sorting: SortingState = [{ id: searchParams.sort || 'lastActivityDateTime', desc: searchParams.direction !== 'asc' }];
  const table = useReactTable<AccountMovement>({
    data: paginatedData?.content || [], columns, state: { sorting },
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const nextSort = tableSortFromSorting(next);
      onSortChange(nextSort.sort, nextSort.direction);
    },
    getCoreRowModel: getCoreRowModel(), manualSorting: true, sortDescFirst: false,
  });

  const columnItems = [
    { key: 'customer', label: t('debts.customer') },
    { key: 'debtType', label: t('debts.movementType') },
    { key: 'description', label: t('debts.description') },
    { key: 'saleNumber', label: t('debts.saleNumber') },
    { key: 'lastActivityDateTime', label: t('debts.transactionDateTime') },
    { key: 'amount', label: t('debts.amount') },
    { key: 'createdByUsername', label: t('debts.createdBy') },
    ...(isSuperAdmin ? [{ key: 'companyName', label: t('debts.company') }] : []),
    ...(canManageDebts ? [{ key: 'actions', label: t('common.actions') }] : []),
  ].map(item => ({ ...item, visible: columnVisibility[item.key] !== false }));

  const emptyMessage = hasAnyFilter ? t('debts.noSearchResults') : t('debts.noDebts');
  const helpContent = <Typography variant="body2">{t('debts.helpSummary')}</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pb: isMobile && paginatedData ? 'calc(61px + env(safe-area-inset-bottom))' : 0 }}>
      <PageHeader
        title={t('nav.debts')}
        icon={<AccountBalanceWalletOutlined />}
        accent="debts"
        mobileMode="hidden"
        titleAccessory={<HelpTooltip helpId="debts-page-help" title={t('help.debts.title')} content={helpContent} maxWidth={500} placement="bottom-start" />}
        description={t('debts.pageDescription')}
        actions={canManageDebts ? (
          <Stack direction="row" spacing={1} sx={{ width: 'auto' }}>
            <PageHeaderActionButton color="success" startIcon={<ArrowDownward />} onClick={handleAddPayment}>{t('debts.addPayment')}</PageHeaderActionButton>
            <PageHeaderActionButton color="error" startIcon={<ArrowUpward />} onClick={handleAddDebt}>{t('debts.addDebt')}</PageHeaderActionButton>
          </Stack>
        ) : undefined}
      />

      <ListSurface sx={{ overflow: { xs: 'hidden', md: 'visible' } }}>
        <ListSurface.Toolbar sx={{ px: { xs: 2, sm: 2, md: 2.5 }, pt: { xs: 1.5, sm: 2, md: 2.5 }, pb: { xs: 0, sm: 2, md: 2.5 } }}>
          <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="stretch">
            <TextField
              placeholder={t('debts.searchPlaceholderShort')}
              aria-label={t('debts.searchPlaceholderShort')}
              value={searchTerm}
              onChange={event => { setSearchTerm(event.target.value); onFilterChange('search', event.target.value || undefined); }}
              size={isMobile ? 'small' : 'medium'}
              fullWidth
              sx={{
                flex: 1,
                minWidth: 0,
                ...(isMobile ? {
                  '& .MuiOutlinedInput-root': {
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    '& fieldset, &:hover fieldset': { border: 0 },
                    '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'primary.main' },
                  },
                } : { '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }),
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                endAdornment: searchTerm ? <InputAdornment position="end"><IconButton aria-label={t('debts.clearSearch')} onClick={() => { setSearchTerm(''); onFilterChange('search', undefined); }}><Clear /></IconButton></InputAdornment> : undefined,
              }}
            />
            <Badge
              badgeContent={appliedFilterCount}
              color="warning"
              invisible={appliedFilterCount === 0}
              overlap={isMobile ? 'circular' : 'rectangular'}
              sx={{ flexShrink: 0, '& .MuiBadge-badge': { fontWeight: 700, border: '2px solid', borderColor: 'background.paper' } }}
            >
              {isMobile ? (
                <IconButton aria-label={`${t('debts.filters')}${appliedFilterCount ? ` (${appliedFilterCount})` : ''}`} aria-expanded={showFilters} aria-controls="debt-filters" onClick={() => setShowFilters(value => !value)} sx={{ width: 44, height: 44, color: 'primary.main' }}>
                  <FilterList />
                </IconButton>
              ) : (
                <Button variant="outlined" aria-label={`${t('debts.filters')}${appliedFilterCount ? ` (${appliedFilterCount})` : ''}`} aria-expanded={showFilters} aria-controls="debt-filters" startIcon={<FilterList />} endIcon={showFilters ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowFilters(value => !value)} sx={{ bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                  {t('debts.filters')}
                </Button>
              )}
            </Badge>
          </Stack>
          <TableFilterPanel id="debt-filters" open={showFilters} onClear={clearFilters} onApply={applyFilters} error={filterError}
            activeFilters={filterFields.filter(field => searchParams[field] !== undefined && searchParams[field] !== '').map(field => ({
              key: field, label: `${t(`debts.filter.${field}`)}: ${searchParams[field]}`, onDelete: () => onFilterChange(field, undefined),
            }))}
            fields={<>
                <TextField label={t('filters.startDate')} type="date" InputLabelProps={{ shrink: true }} value={startDateDraft} onChange={event => { setStartDateDraft(event.target.value); setFilterError(''); }} />
                <TextField label={t('filters.endDate')} type="date" InputLabelProps={{ shrink: true }} value={endDateDraft} onChange={event => { setEndDateDraft(event.target.value); setFilterError(''); }} />
                <TextField label={t('debts.minAmount')} type="number" inputProps={{ min: 0 }} value={minAmountDraft} onChange={event => { setMinAmountDraft(event.target.value); setFilterError(''); }} />
                <TextField label={t('debts.maxAmount')} type="number" inputProps={{ min: 0 }} value={maxAmountDraft} onChange={event => { setMaxAmountDraft(event.target.value); setFilterError(''); }} />
                {isSuperAdmin && <FormControl><InputLabel>{t('debts.createdBy')}</InputLabel><Select value={createdByDraft} label={t('debts.createdBy')} onChange={event => setCreatedByDraft(event.target.value)}><MenuItem value="">{t('common.all')}</MenuItem>{users.map(item => <MenuItem key={item.id} value={item.username}>{item.firstName} {item.lastName}</MenuItem>)}</Select></FormControl>}
                {isSuperAdmin && <FormControl><InputLabel>{t('debts.company')}</InputLabel><Select value={companyDraft} label={t('debts.company')} onChange={event => setCompanyDraft(event.target.value)}><MenuItem value="">{t('common.all')}</MenuItem>{companies.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</Select></FormControl>}
            </>}
          />
        </ListSurface.Toolbar>

        <ListSurface.Content sx={{ position: 'relative' }}>
        <NotebookColumnTab label={t('common.columns')} accent="debts" open={Boolean(columnMenuAnchor)} onClick={event => setColumnMenuAnchor(event.currentTarget)} />
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer>
            <Table sx={{ minWidth: 820 }}>
              <TableHead><TableRow>{table.getHeaderGroups().map(group => group.headers.map(header => (
                <DataTableHeaderCell
                  key={header.id}
                  align={header.id === 'amount' || header.id === 'actions' ? 'right' : 'left'}
                  sort={header.column.getCanSort() ? {
                    active: Boolean(header.column.getIsSorted()),
                    direction: header.column.getIsSorted() === 'desc' ? 'desc' : 'asc',
                    onClick: () => header.column.toggleSorting(),
                  } : undefined}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </DataTableHeaderCell>
              )))}</TableRow></TableHead>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 7 }}><CircularProgress /></TableCell></TableRow>
                  : table.getRowModel().rows.length === 0 ? <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 7 }}>
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                    {!hasAnyFilter && canManageDebts && <Button variant="contained" color="error" startIcon={<ArrowUpward />} onClick={handleAddDebt} sx={{ mt: 2 }}>{t('debts.addDebt')}</Button>}
                  </TableCell></TableRow>
                  : table.getRowModel().rows.map(row => <TableRow key={row.id}>
                    {row.getVisibleCells().map(cell => <TableCell key={cell.id} align={cell.column.id === 'amount' || cell.column.id === 'actions' ? 'right' : 'left'}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>)}
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' }, px: 1.5, pt: 1.5, pb: 1 }}>
          {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
            : !paginatedData?.content.length ? <Typography sx={{ py: 5, textAlign: 'center' }} color="text.secondary">{emptyMessage}</Typography>
              : <Stack spacing={0} divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
                {paginatedData.content.map(debt => {
                  const description = accountMovementDescription(debt, t);
                  return <Box key={debt.id} sx={{ display: 'flex', alignItems: 'stretch', gap: 1.25, py: 1.25 }}>
                    <MovementIcon debt={debt} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Typography component="button" type="button" noWrap aria-label={`${t('debts.openCustomerAccount')}: ${debt.customerName || t('common.unknown')}`} onClick={() => handleCustomerClick(debt)} sx={theme => ({ display: 'block', flex: 1, minWidth: 0, p: 0, border: 0, bgcolor: 'transparent', textAlign: 'left', fontWeight: 700, color: 'primary.main', cursor: 'pointer', ...createTextActionSx(theme) })}>{debt.customerName || t('common.unknown')}</Typography>
                        <MovementAmount debt={debt} t={t} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, mt: .5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {description && <Typography variant="body2">{description}</Typography>}
                          {debt.saleNumber && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t('debts.saleNumber')}: {debt.saleNumber}</Typography>}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: description || debt.saleNumber ? .75 : 0 }}>{formatDateTime(debt.lastActivityDateTime || debt.transactionDateTime || debt.transactionDate, language, { dateStyle: 'medium', timeStyle: 'short' })}</Typography>
                        </Box>
                        {canManageDebts && (isSaleLinked(debt)
                          ? <Tooltip title={t('debts.saleMovementReadOnly')}><IconButton onClick={handleSaleMovementLocked} aria-label={t('debts.saleMovementReadOnly')} sx={{ width: 44, height: 44, flexShrink: 0, color: 'text.secondary' }}><LockOutlined /></IconButton></Tooltip>
                          : <DebtActionsMenu debt={toEditableDebt(debt)} onEdit={() => handleEditDebt(debt)} onDelete={() => handleDeleteDebt(debt)} />)}
                      </Box>
                    </Box>
                  </Box>;
                })}
              </Stack>}
        </Box>

        {!loading && totalElements === 0 && !hasAnyFilter && canManageDebts && <Box sx={{ display: { xs: 'block', md: 'none' }, pb: 3, textAlign: 'center' }}><Button variant="contained" color="error" startIcon={<ArrowUpward />} onClick={handleAddDebt}>{t('debts.addDebt')}</Button></Box>}
        </ListSurface.Content>
        <ListSurface.Footer>
        {paginatedData && (isMobile || totalElements > 0) && <PaginationFooter pageIndex={page} pageSize={pageSize} totalPages={totalPages} totalElements={totalElements} showingLabel={t('common.showing')} ofLabel={t('common.of')} pageSizeLabel={t('common.rowsPerPage')} mobileCompact previousPageLabel={t('debts.previousPage')} nextPageLabel={t('debts.nextPage')} onPageChange={nextPage => onPaginationModelChange({ page: nextPage, pageSize })} onPageSizeChange={nextSize => onPaginationModelChange({ page: 0, pageSize: nextSize })} />}
        </ListSurface.Footer>
      </ListSurface>

      <Snackbar
        open={saleMovementNoticeOpen}
        autoHideDuration={4500}
        onClose={() => setSaleMovementNoticeOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 'calc(76px + env(safe-area-inset-bottom))', md: 24 } }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setSaleMovementNoticeOpen(false)} sx={{ width: '100%' }}>
          {t('debts.saleMovementReadOnly')}
        </Alert>
      </Snackbar>

      <DebtDialog open={debtDialogOpen} onClose={() => { setDebtDialogOpen(false); setSelectedDebt(undefined); }} debt={selectedDebt} onSuccess={() => {}} />
      <PaymentDialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} onSuccess={() => {}} />
      <DeleteDebtDialog open={Boolean(debtToDelete)} onClose={() => setDebtToDelete(null)} debt={debtToDelete} onSuccess={() => {}} />
      <ColumnVisibilityMenu side anchorEl={columnMenuAnchor} onClose={() => setColumnMenuAnchor(null)} onToggle={key => setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }))} items={columnItems} />
    </Box>
  );
});

export default DebtList;
